import { PLATFORM_MAP, GENRE_MAP } from '../data/constants';

export const detectPlatform = (platforms) => {
  const gamePlatform = platforms?.find(p => PLATFORM_MAP[p.platform.name]);
  return gamePlatform ? PLATFORM_MAP[gamePlatform.platform.name] : 'PC';
};

export const mapGenresToTags = (genres) => {
  return genres?.map(g => GENRE_MAP[g.name] || null).filter(Boolean) || [];
};

export const calculateStats = (games) => {
  return {
    total: games.length,
    completed: games.filter(g => g.status === 'completed').length,
    playing: games.filter(g => g.status === 'playing').length,
    avgRating: games.length > 0
      ? (games.reduce((acc, g) => acc + g.rating, 0) / games.length).toFixed(1)
      : 0,
    platinados: games.filter(g => g.platinado).length,
    favorites: games.filter(g => g.favorite).length
  };
};

// Somente jogos ativos (não excluídos) devem alimentar biblioteca/estatísticas.
export const activeGames = (games) => games.filter(g => !g.deletedAt);
export const trashedGames = (games) => games.filter(g => g.deletedAt);

export const filterGames = (games, filters = {}) => {
  const {
    searchTerm = '',
    status = 'all',
    platform = 'all',
    showArchived = false
  } = filters;

  return games.filter(game => {
    if (!showArchived && game.archived) return false;
    const matchesSearch = game.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = status === 'all' || game.status === status;
    const matchesPlatform = platform === 'all' || game.platform === platform;
    return matchesSearch && matchesStatus && matchesPlatform;
  });
};

export const pushStatusHistory = (game, newStatus) => {
  const history = Array.isArray(game.statusHistory) ? game.statusHistory : [];
  if (game.status === newStatus) return history;
  return [...history, { from: game.status || null, to: newStatus, date: new Date().toISOString() }];
};

export const findPossibleDuplicate = (games, name, excludeId = null) => {
  const normalized = name.trim().toLowerCase();
  if (normalized.length < 2) return null;
  return activeGames(games).find(g => g.id !== excludeId && g.name.trim().toLowerCase() === normalized) || null;
};

export const playedHoursLabel = (game) => {
  const h = Number(game.hoursPlayed) || 0;
  const m = Number(game.minutesPlayed) || 0;
  if (!h && !m) return null;
  return `${h > 0 ? `${h}h` : ''}${m > 0 ? ` ${m}min` : ''}`.trim();
};

export const getYearRetrospective = (games, year) => {
  const yearNum = Number(year);
  const finishedThisYear = activeGames(games).filter(g => g.dateFinished && new Date(`${g.dateFinished}T12:00:00`).getFullYear() === yearNum);
  const sortedByDate = [...finishedThisYear].sort((a, b) => new Date(a.dateFinished) - new Date(b.dateFinished));
  const bestRated = [...finishedThisYear].sort((a, b) => (b.rating || 0) - (a.rating || 0))[0] || null;

  const platformCounts = finishedThisYear.reduce((acc, g) => { acc[g.platform] = (acc[g.platform] || 0) + 1; return acc; }, {});
  const topPlatform = Object.entries(platformCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || null;

  const genreCounts = {};
  finishedThisYear.forEach(g => (g.tags || []).forEach(tag => { genreCounts[tag] = (genreCounts[tag] || 0) + 1; }));
  const topGenre = Object.entries(genreCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || null;

  return {
    year: yearNum,
    completedCount: finishedThisYear.length,
    bestRated,
    topPlatform,
    topGenre,
    firstGame: sortedByDate[0] || null,
    lastGame: sortedByDate[sortedByDate.length - 1] || null,
    platinadosCount: finishedThisYear.filter(g => g.platinado).length
  };
};

export const getMonthlySummary = (games) => {
  const now = new Date();
  const isThisMonth = (dateStr) => {
    if (!dateStr) return false;
    const date = new Date(`${dateStr}T12:00:00`);
    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  };
  const active = activeGames(games);
  const finishedThisMonth = active.filter(g => isThisMonth(g.dateFinished));
  const startedThisMonth = active.filter(g => (g.statusHistory || []).some(h => h.to === 'playing' && isThisMonth(h.date?.slice(0, 10))));

  const hours = finishedThisMonth.reduce((acc, g) => acc + (Number(g.hoursPlayed) || 0), 0);
  const highlight = [...finishedThisMonth].sort((a, b) => (b.rating || 0) - (a.rating || 0))[0] || null;

  return {
    startedCount: startedThisMonth.length,
    finishedCount: finishedThisMonth.length,
    hours,
    highlight,
    monthLabel: now.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
  };
};
